import { useState } from 'react';
import { colors } from '../constants/colors';
import { X, Loader2, CheckCircle, FileText } from 'lucide-react';
import { getShipmentRates, buyShipmentLabel } from '../api/client';
import type { Shipment, ShippingRateQuote, BuyLabelResponse } from '../types';
import { PackagingSelector, usePackagingState, isPackagingValid, buildRateData } from './PackagingSelector';

const CARRIER_LOGOS: Record<string, string> = {
  USPS: '/img/logos/carrier-usps.svg',
  UPS: '/img/logos/carrier-ups.svg',
  FedEx: '/img/logos/carrier-fx.svg',
};

const SERVICE_DISPLAY_NAMES: Record<string, Record<string, string>> = {
  USPS: {
    GroundAdvantage: 'Ground Advantage',
    Priority: 'Priority Mail',
    Express: 'Priority Mail Express',
    First: 'First Class',
    ParcelSelect: 'Parcel Select',
  },
  UPS: {
    Ground: 'Ground',
    '3DaySelect': '3-Day Select',
    '2ndDayAir': '2nd Day Air',
    '2ndDayAirAM': '2nd Day Air AM',
    NextDayAirSaver: 'Next Day Air Saver',
    NextDayAir: 'Next Day Air',
    NextDayAirEarlyAM: 'Next Day Air Early AM',
    UPSGroundsaverGreaterThan1lb: 'Ground Saver',
  },
  FedEx: {
    FEDEX_GROUND: 'Ground',
    FEDEX_2_DAY: '2 Day',
    FEDEX_2_DAY_AM: '2 Day AM',
    FEDEX_EXPRESS_SAVER: 'Express Saver',
    PRIORITY_OVERNIGHT: 'Priority Overnight',
    STANDARD_OVERNIGHT: 'Standard Overnight',
    FIRST_OVERNIGHT: 'First Overnight',
    SMART_POST: 'SmartPost',
  },
};

function getServiceDisplayName(carrier: string, service: string): string {
  return SERVICE_DISPLAY_NAMES[carrier]?.[service]
    ?? service.replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2');
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
}

interface ShipmentLabelModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  shipment: Shipment;
}

export function ShipmentLabelModal({
  isOpen,
  onClose,
  onSuccess,
  shipment,
}: ShipmentLabelModalProps) {
  const [pkgState, setPkgState] = usePackagingState(
    shipment.weightOz?.toString(),
    shipment.lengthIn?.toString(),
    shipment.widthIn?.toString(),
    shipment.heightIn?.toString()
  );
  const [rates, setRates] = useState<ShippingRateQuote[]>([]);
  const [selectedRateId, setSelectedRateId] = useState<string | null>(null);
  const [isLoadingRates, setIsLoadingRates] = useState(false);
  const [isBuyingLabel, setIsBuyingLabel] = useState(false);
  const [labelError, setLabelError] = useState<string | null>(null);
  const [purchaseResult, setPurchaseResult] = useState<BuyLabelResponse | null>(null);
  const [lastFetchedKey, setLastFetchedKey] = useState('');

  if (!isOpen) return null;

  const dimensionsKey = JSON.stringify(buildRateData(pkgState));
  const dimensionsValid = isPackagingValid(pkgState);
  const dimensionsChanged = dimensionsValid && dimensionsKey !== lastFetchedKey;
  const selectedRate = rates.find(r => r.rateId === selectedRateId);

  const handleGetRates = async () => {
    if (!dimensionsValid) {
      setLabelError(pkgState.mode === 'carrier'
        ? 'Please select a carrier package and enter weight'
        : 'Please fill in all package dimensions and weight');
      return;
    }
    try {
      setIsLoadingRates(true);
      setLabelError(null);
      setRates([]);
      setSelectedRateId(null);

      const response = await getShipmentRates(shipment.id, buildRateData(pkgState));
      setRates(response.rates);
      setLastFetchedKey(dimensionsKey);
      if (response.rates.length === 0) {
        setLabelError('No rates available for this package. Check dimensions and origin address.');
      }
    } catch (err) {
      setLabelError(err instanceof Error ? err.message : 'Failed to get rates');
    } finally {
      setIsLoadingRates(false);
    }
  };

  const handleBuyLabel = async () => {
    if (!selectedRateId) return;
    try {
      setIsBuyingLabel(true);
      setLabelError(null);
      const result = await buyShipmentLabel(shipment.id, { rateId: selectedRateId });
      setPurchaseResult(result);
      onSuccess();
    } catch (err) {
      setLabelError(err instanceof Error ? err.message : 'Failed to buy label');
    } finally {
      setIsBuyingLabel(false);
    }
  };

  return (
    <div style={styles.overlay}>
      <div style={purchaseResult ? styles.modalSuccess : styles.modal}>
        <div style={styles.header}>
          <h2 style={styles.title}>{purchaseResult ? 'Label Created' : 'Buy Shipping Label'}</h2>
          <button onClick={onClose} style={styles.closeButton}>
            <X size={20} />
          </button>
        </div>

        {purchaseResult ? (
          <div style={styles.successBody}>
            <div style={styles.successScreen}>
              <CheckCircle size={48} color={colors.status.success} />
              <h3 style={styles.successTitle}>Label purchased successfully</h3>
              <div style={styles.successDetails}>
                <div style={styles.successRow}>
                  <span style={styles.successLabel}>Carrier</span>
                  <span style={styles.successValue}>
                    {CARRIER_LOGOS[purchaseResult.carrier] && (
                      <img src={CARRIER_LOGOS[purchaseResult.carrier]} alt={purchaseResult.carrier} style={styles.carrierLogoSmall} />
                    )}
                    {purchaseResult.carrier} — {getServiceDisplayName(purchaseResult.carrier, purchaseResult.service)}
                  </span>
                </div>
                <div style={styles.successRow}>
                  <span style={styles.successLabel}>Tracking</span>
                  <span style={{ ...styles.successValue, fontFamily: 'monospace' }}>{purchaseResult.trackingNumber}</span>
                </div>
                <div style={styles.successRow}>
                  <span style={styles.successLabel}>Cost</span>
                  <span style={styles.successValue}>{formatCurrency(purchaseResult.postageCost)}</span>
                </div>
              </div>
              <a
                href={purchaseResult.labelUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={styles.downloadLabelButton}
              >
                <FileText size={18} />
                Print Label (PDF)
              </a>
              <button style={styles.doneButton} onClick={onClose}>
                Done
              </button>
            </div>
          </div>
        ) : (
          <div style={styles.body}>
            <PackagingSelector state={pkgState} onChange={(s) => { setPkgState(s); setRates([]); setLastFetchedKey(''); }} />

            <div style={styles.section}>
              <button
                style={{
                  ...styles.getRatesButton,
                  ...(((!dimensionsValid || !dimensionsChanged) && !isLoadingRates) ? { opacity: 0.4, cursor: 'not-allowed' } : {}),
                }}
                onClick={handleGetRates}
                disabled={!dimensionsValid || !dimensionsChanged || isLoadingRates}
              >
                {isLoadingRates ? (
                  <>
                    <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                    Getting Rates...
                  </>
                ) : (
                  rates.length > 0 ? 'Update Rates' : 'Get Rates'
                )}
              </button>
            </div>

            {labelError && <div style={styles.errorBox}>{labelError}</div>}

            {rates.length > 0 && (
              <div style={styles.section}>
                <h3 style={styles.sectionTitle}>Select a Rate</h3>
                <div style={styles.ratesList}>
                  {rates.map(rate => (
                    <button
                      key={rate.rateId}
                      style={{
                        ...styles.rateCard,
                        ...(selectedRateId === rate.rateId ? styles.rateCardSelected : {}),
                      }}
                      onClick={() => setSelectedRateId(rate.rateId)}
                    >
                      <div style={styles.rateInfo}>
                        <div style={styles.rateCarrierRow}>
                          {CARRIER_LOGOS[rate.carrier] && (
                            <img src={CARRIER_LOGOS[rate.carrier]} alt={rate.carrier} style={styles.rateCarrierLogo} />
                          )}
                          <span style={styles.rateCarrier}>{rate.carrier}</span>
                        </div>
                        <span style={styles.rateService}>{getServiceDisplayName(rate.carrier, rate.service)}</span>
                        {rate.estimatedDays > 0 && (
                          <span style={styles.rateDelivery}>
                            ~{rate.estimatedDays} day{rate.estimatedDays !== 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                      <span style={styles.rateCost}>{formatCurrency(rate.cost)}</span>
                    </button>
                  ))}
                </div>

                <p style={styles.costInfoText}>
                  The cost of the shipping label will be deducted from your account balance.
                </p>

                <button
                  style={{
                    ...styles.buyButton,
                    ...((!selectedRateId || isBuyingLabel) ? { opacity: 0.5, cursor: 'not-allowed' } : {}),
                  }}
                  onClick={handleBuyLabel}
                  disabled={!selectedRateId || isBuyingLabel}
                >
                  {isBuyingLabel ? (
                    <>
                      <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />
                      Purchasing...
                    </>
                  ) : (
                    <>Buy Label{selectedRate ? ` — ${formatCurrency(selectedRate.cost)}` : ''}</>
                  )}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: colors.white,
    borderRadius: '16px',
    width: '100%',
    maxWidth: '480px',
    maxHeight: '90vh',
    overflow: 'hidden',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
    display: 'flex',
    flexDirection: 'column',
  },
  modalSuccess: {
    backgroundColor: colors.white,
    borderRadius: '16px',
    width: '100%',
    maxWidth: '480px',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: `1px solid ${colors.gray[300]}`,
    flexShrink: 0,
  },
  title: {
    fontSize: '18px',
    fontWeight: 600,
    margin: 0,
    color: colors.ink.black,
  },
  closeButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: colors.gray[600],
    padding: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  body: {
    padding: '20px 24px',
    overflowY: 'auto',
    flex: 1,
    minHeight: 0,
  },
  section: {
    marginBottom: '20px',
  },
  sectionTitle: {
    fontSize: '13px',
    fontWeight: 600,
    color: colors.gray[600],
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    margin: '0 0 10px 0',
  },
  weightField: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    maxWidth: '160px',
  },
  dimLabel: {
    fontSize: '12px',
    fontWeight: 600,
    color: colors.gray[600],
  },
  dimInput: {
    width: '100%',
    padding: '8px 10px',
    border: `1px solid ${colors.gray[300]}`,
    borderRadius: '8px',
    fontSize: '14px',
    color: colors.ink.black,
    boxSizing: 'border-box',
  },
  dimGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    gap: '10px',
    marginBottom: '10px',
  },
  dimField: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  getRatesButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    width: '100%',
    padding: '10px 16px',
    fontSize: '13px',
    fontWeight: 600,
    color: colors.brand.blue,
    backgroundColor: colors.brand.blue10,
    border: `1px solid ${colors.brand.blue}`,
    borderRadius: '8px',
    cursor: 'pointer',
  },
  errorBox: {
    padding: '10px 12px',
    marginBottom: '12px',
    backgroundColor: '#FEE2E2',
    color: '#DC2626',
    borderRadius: '8px',
    fontSize: '13px',
  },
  ratesList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    marginBottom: '12px',
    maxHeight: '240px',
    overflowY: 'auto',
  },
  rateCard: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px',
    backgroundColor: colors.gray[100],
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: colors.gray[300],
    borderRadius: '10px',
    cursor: 'pointer',
    textAlign: 'left',
    width: '100%',
  },
  rateCardSelected: {
    backgroundColor: colors.brand.blue10,
    borderColor: colors.brand.blue,
  },
  rateInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  rateCarrierRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  rateCarrierLogo: {
    height: '16px',
    width: 'auto',
    objectFit: 'contain',
  },
  rateCarrier: {
    fontSize: '13px',
    fontWeight: 600,
    color: colors.ink.black,
  },
  rateService: {
    fontSize: '12px',
    color: colors.gray[600],
  },
  rateDelivery: {
    fontSize: '11px',
    color: colors.gray[600],
  },
  rateCost: {
    fontSize: '15px',
    fontWeight: 700,
    color: colors.ink.black,
  },
  costInfoText: {
    fontSize: '12px',
    color: colors.gray[600],
    textAlign: 'center',
    margin: '0 0 12px 0',
    lineHeight: 1.4,
  },
  buyButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    width: '100%',
    padding: '12px 20px',
    fontSize: '14px',
    fontWeight: 600,
    color: colors.white,
    backgroundColor: colors.brand.blue,
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
  },
  successBody: {
    padding: '24px',
  },
  successScreen: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px',
  },
  successTitle: {
    fontSize: '18px',
    fontWeight: 600,
    color: colors.ink.black,
    margin: 0,
  },
  successDetails: {
    width: '100%',
    backgroundColor: colors.gray[100],
    borderRadius: '10px',
    padding: '14px 16px',
    marginTop: '4px',
  },
  successRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: '14px',
    padding: '6px 0',
  },
  successLabel: {
    color: colors.gray[600],
    fontWeight: 500,
  },
  successValue: {
    color: colors.ink.black,
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  carrierLogoSmall: {
    height: '16px',
    width: 'auto',
    objectFit: 'contain',
  },
  downloadLabelButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    width: '100%',
    padding: '12px 20px',
    fontSize: '14px',
    fontWeight: 600,
    color: colors.white,
    backgroundColor: '#22C55E',
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
    textDecoration: 'none',
    boxSizing: 'border-box',
    marginTop: '4px',
  },
  doneButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    width: '100%',
    padding: '12px 20px',
    fontSize: '14px',
    fontWeight: 600,
    color: colors.gray[700],
    backgroundColor: colors.gray[100],
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
    marginTop: '4px',
  },
};
